import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, Filter, X, ChevronLeft, ChevronRight, Plus, Tag, RefreshCw, Bookmark, BookmarkPlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CATEGORIES, STATUSES, OUTREACH_STAGES, STATUS_TONE, STAGE_TONE, formatRel, initials } from "./constants";
import DomainBanner from "./DomainBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminCrmContactsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("");
  const [category, setCategory] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Selection / bulk
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkStage, setBulkStage] = useState("");
  const [bulkAddTag, setBulkAddTag] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  // Saved views (Round 4)
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/crm/contacts`, {
        params: {
          page,
          per_page: 25,
          q: q || undefined,
          status: status || undefined,
          outreach_stage: stage || undefined,
          category: category || undefined,
          follow_up: followUp || undefined,
        },
      });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load CRM contacts");
    } finally {
      setLoading(false);
    }
  }, [page, q, status, stage, category, followUp]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/crm/contacts/stats`);
      setStats(data);
    } catch {
      /* non-blocking */
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadSavedViews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/crm/saved-views`);
      setSavedViews(data.items || []);
    } catch { /* non-blocking */ }
  }, []);
  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [q, status, stage, category, followUp]);

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((c) => c.id)));
  const toggleOne = (id) => setSelected((p) => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const runBulk = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/admin/crm/contacts/bulk-update`, {
        contact_ids: [...selected],
        status: bulkStatus || undefined,
        outreach_stage: bulkStage || undefined,
        add_tags: bulkAddTag ? [bulkAddTag.trim()] : undefined,
      });
      toast.success(`Updated ${selected.size} contact(s)`);
      setBulkOpen(false);
      setBulkStatus(""); setBulkStage(""); setBulkAddTag("");
      setSelected(new Set());
      load(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runMigration = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/crm/contacts/migrate?force=false`, {});
      toast.success(data.status === "ok" ? `Imported ${data.created}, skipped ${data.skipped}` : "Already migrated");
      load(); loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    }
  };

  const activeFilterCount = useMemo(
    () => [status, stage, category, followUp].filter(Boolean).length,
    [status, stage, category, followUp]
  );

  const hasActiveFilters = activeFilterCount > 0 || q.trim() !== "";

  const applyView = (view) => {
    const f = view.filters || {};
    setQ(f.q || "");
    setStatus(f.status || "");
    setStage(f.outreach_stage || "");
    setCategory(f.category || "");
    setFollowUp(f.follow_up || "");
    setActiveViewId(view.id);
    toast.success(`View "${view.name}" applied`);
  };

  const clearView = () => {
    setQ(""); setStatus(""); setStage(""); setCategory(""); setFollowUp("");
    setActiveViewId(null);
  };

  const saveView = async () => {
    const name = saveViewName.trim();
    if (!name) return;
    try {
      const { data } = await axios.post(`${API}/admin/crm/saved-views`, {
        name,
        filters: {
          q: q || undefined,
          status: status || undefined,
          outreach_stage: stage || undefined,
          category: category || undefined,
          follow_up: followUp || undefined,
        },
      });
      toast.success(`Saved view "${data.name}"`);
      setSaveViewOpen(false);
      setSaveViewName("");
      setActiveViewId(data.id);
      loadSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteView = async (view) => {
    if (!window.confirm(`Delete saved view "${view.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/crm/saved-views/${view.id}`);
      toast.success("View deleted");
      if (activeViewId === view.id) setActiveViewId(null);
      loadSavedViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="crm-contacts-page">
      <DomainBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CRM · Contacts</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {stats ? `${stats.total} contacts` : "Loading…"}
            {stats?.follow_ups_due > 0 && (
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-xs">
                {stats.follow_ups_due} follow-up due
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={runMigration} data-testid="crm-migrate-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Sync from website
          </Button>
          <Link to="/admin/crm/contacts/new" data-testid="crm-new-contact-link">
            <Button size="sm" className="bg-cyan-700 hover:bg-cyan-800 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New contact
            </Button>
          </Link>
        </div>
      </div>

      {/* Saved views row */}
      <div className="flex flex-wrap items-center gap-2" data-testid="crm-saved-views-row">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">Views:</span>
        <button
          onClick={clearView}
          className={`text-xs px-2.5 py-1 rounded-full ring-1 transition ${
            !activeViewId
              ? "bg-cyan-700 text-white ring-cyan-700"
              : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
          }`}
          data-testid="crm-view-all"
        >
          All
        </button>
        {savedViews.map((v) => (
          <div key={v.id} className="inline-flex items-stretch rounded-full ring-1 ring-slate-200 bg-white overflow-hidden">
            <button
              onClick={() => applyView(v)}
              className={`text-xs px-2.5 py-1 inline-flex items-center gap-1 ${
                activeViewId === v.id ? "bg-indigo-50 text-indigo-700" : "text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`crm-view-${v.id}`}
            >
              <Bookmark className="w-3 h-3" /> {v.name}
            </button>
            <button
              onClick={() => deleteView(v)}
              className="px-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-50 border-l border-slate-200"
              data-testid={`crm-view-delete-${v.id}`}
              title="Delete view"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {hasActiveFilters && (
          <button
            onClick={() => { setSaveViewOpen(true); setSaveViewName(""); }}
            className="text-xs px-2.5 py-1 rounded-full bg-white text-cyan-700 ring-1 ring-cyan-200 hover:bg-cyan-50 inline-flex items-center gap-1"
            data-testid="crm-view-save-toggle"
          >
            <BookmarkPlus className="w-3 h-3" /> Save current view
          </button>
        )}
      </div>

      {/* Save view inline form */}
      {saveViewOpen && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="crm-view-save-form">
          <input
            type="text"
            value={saveViewName}
            onChange={(e) => setSaveViewName(e.target.value)}
            placeholder="Name this view, e.g. Pilot pipeline"
            maxLength={80}
            className="flex-1 min-w-[200px] text-sm rounded-md border border-slate-300 px-3 py-1.5 bg-white"
            data-testid="crm-view-name-input"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") saveView(); }}
          />
          <Button size="sm" onClick={saveView} disabled={!saveViewName.trim()} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-view-save-submit">
            Save view
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSaveViewOpen(false)}>Cancel</Button>
        </div>
      )}

      {/* Search + filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, organization, city…"
            className="w-full text-sm rounded-lg border border-slate-200 pl-9 pr-3 py-2 bg-white focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            data-testid="crm-search-input"
          />
        </div>
        <Button
          variant={showFilters ? "default" : "outline"}
          size="sm"
          onClick={() => setShowFilters((v) => !v)}
          data-testid="crm-toggle-filters"
        >
          <Filter className="w-4 h-4 mr-1.5" />
          Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
        </Button>
        {selected.size > 0 && (
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="crm-bulk-btn">
            <Tag className="w-4 h-4 mr-1.5" /> Bulk update ({selected.size})
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUSES} testid="crm-filter-status" />
          <FilterSelect label="Outreach stage" value={stage} onChange={setStage} options={OUTREACH_STAGES} testid="crm-filter-stage" />
          <FilterSelect label="Category" value={category} onChange={setCategory} options={CATEGORIES} testid="crm-filter-category" />
          <FilterSelect label="Follow-up" value={followUp} onChange={setFollowUp} options={["due", "overdue", "none"]} testid="crm-filter-followup" />
          {activeFilterCount > 0 && (
            <button
              onClick={() => { setStatus(""); setStage(""); setCategory(""); setFollowUp(""); }}
              className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 col-span-full justify-end"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          )}
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="crm-filter-chips">
          {status && (
            <FilterChip label={`Status: ${status}`} onRemove={() => setStatus("")} testid="crm-chip-status" />
          )}
          {stage && (
            <FilterChip label={`Stage: ${stage}`} onRemove={() => setStage("")} testid="crm-chip-stage" />
          )}
          {category && (
            <FilterChip label={`Category: ${category}`} onRemove={() => setCategory("")} testid="crm-chip-category" />
          )}
          {followUp && (
            <FilterChip label={`Follow-up: ${followUp}`} onRemove={() => setFollowUp("")} testid="crm-chip-followup" />
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700 bg-red-50">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <p className="text-sm">No contacts match these filters.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={runMigration}>
              <RefreshCw className="w-4 h-4 mr-1.5" /> Try syncing from website
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="crm-contacts-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="crm-select-all" />
                  </th>
                  <th className="px-3 py-2.5 font-semibold">Name</th>
                  <th className="px-3 py-2.5 font-semibold">Organization</th>
                  <th className="px-3 py-2.5 font-semibold">Category</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold">Stage</th>
                  <th className="px-3 py-2.5 font-semibold">Score</th>
                  <th className="px-3 py-2.5 font-semibold">Next follow-up</th>
                  <th className="px-3 py-2.5 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50" data-testid={`crm-row-${c.id}`}>
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`crm-row-check-${c.id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <Link to={`/admin/crm/contacts/${c.id}`} className="flex items-center gap-3 group">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 ring-1 ring-cyan-200 flex items-center justify-center text-xs font-semibold text-cyan-800">
                          {initials(c.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 group-hover:text-cyan-700 truncate">{c.name}</div>
                          {c.email && <div className="text-xs text-slate-500 truncate">{c.email}</div>}
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap truncate max-w-[200px]">{c.organization || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 ring-1 ring-slate-200">{c.category}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-md ring-1 ${STATUS_TONE[c.status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-md ring-1 ${STAGE_TONE[c.outreach_stage] || "bg-slate-100 text-slate-700 ring-slate-200"}`}>
                        {c.outreach_stage}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-slate-700">{c.lead_score}</td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap">
                      {c.next_follow_up_at ? new Date(c.next_follow_up_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatRel(c.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm">
            <div className="text-slate-500">Page {page} of {totalPages} · {total} total</div>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk modal */}
      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBulkOpen(false)}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
            data-testid="crm-bulk-modal"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Bulk update</h3>
            <p className="text-sm text-slate-500 mb-4">{selected.size} contact(s) selected</p>
            <div className="space-y-3">
              <FilterSelect label="Set status" value={bulkStatus} onChange={setBulkStatus} options={STATUSES} testid="crm-bulk-status" />
              <FilterSelect label="Set outreach stage" value={bulkStage} onChange={setBulkStage} options={OUTREACH_STAGES} testid="crm-bulk-stage" />
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Add tag</span>
                <input
                  type="text"
                  value={bulkAddTag}
                  onChange={(e) => setBulkAddTag(e.target.value)}
                  placeholder="e.g. q1-prospect"
                  className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2"
                  data-testid="crm-bulk-tag-input"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={runBulk} disabled={bulkBusy} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="crm-bulk-submit">
                {bulkBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full text-sm rounded-md border border-slate-300 px-3 py-2 bg-white"
        data-testid={testid}
      >
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function FilterChip({ label, onRemove, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
      data-testid={testid}
    >
      {label}
      <button
        onClick={onRemove}
        className="hover:bg-indigo-100 rounded-full p-0.5"
        aria-label={`Remove ${label}`}
        data-testid={`${testid}-remove`}
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
